// ====================================================
// src/components/block/liveStreamManagement/videoEffects/VideoEffect.ts
//
// Base commune à tous les effets vidéo du panneau "Effets vidéo" de
// SourceConfigModal (arrière-plan, flou, makeup, avatar...). Un nouvel effet
// s'ajoute en créant une classe qui étend VideoEffect et en l'inscrivant dans
// registry.ts — la modale, elle, ne connaît que cette interface : elle boucle
// sur VIDEO_EFFECTS et ne référence jamais un effet par son nom.
// ====================================================

import { reducedSize, HIDE_BODY_BLUR_SCALE } from './faceTrackingBudget';
import React from 'react';
import { FaceTrackingResult } from './FaceTracker';
import { HandTrackingResult } from './HandTracker';

export interface VideoEffectControlProps<TConfig> {
  config: TConfig;
  onChange: (config: TConfig) => void;
}

export interface VideoEffectCompositeArgs<TConfig> {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  /** Source des pixels de la frame courante — caméra ou HLS, peu importe : c'est toujours
   * la <video> actuellement affichée dans "Aperçu" (visible ou masquée derrière le canvas). */
  frame: HTMLVideoElement;
  /** Masque de détourage (blanc = personne, alpha = confiance), déjà remis à l'échelle du
   * canvas — cf. drawPersonCutout ci-dessous pour s'en servir. Null tant qu'aucune frame n'a
   * encore été segmentée (modèle en cours de chargement, ou détourage indisponible). */
  personAlpha: HTMLCanvasElement | null;
  /** Points de CHAQUE visage détecté (coordonnées normalisées 0..1) — cf. FaceTracker.ts,
   * needsFaceTracking. Tableau vide tant qu'aucun visage n'a encore été détecté (modèle
   * en cours de chargement, ou aucun visage dans le cadre) — jamais null, pour ne pas
   * forcer chaque effet consommateur à re-tester la nullité en plus de la longueur. */
  faceLandmarks: FaceTrackingResult[];
  /** Points de CHAQUE main détectée (coordonnées normalisées 0..1) — cf. HandTracker.ts,
   * needsHandTracking. Même contrat que faceLandmarks : tableau vide tant qu'aucune main
   * n'a encore été détectée, jamais null. */
  handLandmarks: HandTrackingResult[];
  /** True si l'effet actif du groupe "face" veut que la personne n'apparaisse PAS dans
   * la frame finale (cf. VideoEffect.wantsPersonHidden) — un effet "surroundings" (ex.
   * Arrière-plan) doit alors NE PAS découper/coller la personne par-dessus son fond
   * (cf. drawPersonCutout), pour que ce fond reste visible entier à la place. Toujours
   * false si personAlpha est null (rien à découper de toute façon). */
  hidePerson: boolean;
  /** Canvas de travail hors-écran, réutilisé à chaque frame par le compositeur — évite d'en
   * recréer un par effet et par frame. */
  scratch: HTMLCanvasElement;
  config: TConfig;
}

/**
 * Groupe d'exclusivité mutuelle — deux effets du MÊME groupe ne peuvent pas être actifs
 * en même temps (activer l'un désactive l'autre), mais deux effets de groupes
 * DIFFÉRENTS se cumulent librement. "surroundings" (Arrière-plan, Flou) traite ce qui
 * entoure la personne et peint la base de la frame ; "face" (Makeup, Avatar) traite la
 * personne elle-même et se pose en calque par-dessus cette base, sans jamais la
 * repeindre entièrement — cf. l'ordre de composition dans compositeFrame.ts.
 */
export type VideoEffectGroup = 'surroundings' | 'face';

export abstract class VideoEffect<TConfig = unknown> {
  abstract readonly id: string;
  abstract readonly label: string;
  abstract readonly icon: React.FC<{ style?: React.CSSProperties }>;
  abstract readonly defaultConfig: TConfig;
  abstract readonly group: VideoEffectGroup;

  // Effet pas encore implémenté côté pipeline (avatar) : ses contrôles restent visibles
  // mais grisés, cf. disabledReason affiché à côté du libellé.
  readonly disabled: boolean = false;
  readonly disabledReason?: string;

  /**
   * Cet effet a-t-il besoin du détourage (masque personne/fond) pour se dessiner ?
   * Pilote le compositeur de SourceConfigModal : tant qu'aucun effet actif ne le
   * demande, la préview reste la <video> brute (pas de coût de segmentation).
   */
  readonly needsSegmentation: boolean = false;

  /**
   * Le détourage est-il nécessaire POUR CETTE CONFIG ? Consulté par la boucle de rendu du
   * flux publié (VideoEffectsPipelineManager), à chaque image, pour ne payer la
   * segmentation que lorsqu'elle sert. Par défaut : la réponse de `needsSegmentation`.
   * Avatar la surcharge — son détourage ne sert qu'à « Cacher le corps ».
   */
  needsSegmentationFor(_config: TConfig): boolean {
    return this.needsSegmentation;
  }

  /**
   * Cet effet a-t-il besoin du suivi du visage (points de repère) pour se dessiner ?
   * Même principe que needsSegmentation, mais pour une tâche MediaPipe distincte
   * (FaceLandmarker, pas ImageSegmenter) — cf. FaceTracker.ts.
   */
  readonly needsFaceTracking: boolean = false;

  /**
   * Cet effet a-t-il besoin du suivi des mains (points de repère) pour se dessiner ?
   * Même principe que needsFaceTracking, mais pour une tâche MediaPipe distincte
   * (HandLandmarker) — cf. HandTracker.ts.
   */
  readonly needsHandTracking: boolean = false;

  /**
   * Cet effet (du groupe "face") veut-il que la personne soit absente de la frame
   * finale — cf. VideoEffectCompositeArgs.hidePerson, qui répercute la réponse vers
   * l'effet "surroundings" actif ? Faux par défaut (la quasi-totalité des effets
   * "face" se posent SUR la personne, ils ne veulent pas qu'elle disparaisse) —
   * seul un effet comme Avatar, qui REMPLACE la personne par un modèle 3D, a une
   * raison de répondre vrai (cf. AvatarEffect.wantsPersonHidden, config.hideBody).
   * Sans effet pour un effet du groupe "surroundings" — jamais consulté pour eux.
   */
  wantsPersonHidden(_config: TConfig): boolean {
    return false;
  }

  /**
   * Contribution de cet effet au filtre CSS appliqué à la <video> de préview — pour un
   * effet qui n'a pas besoin de détourage et se contente d'un ajustement global de l'image.
   * Retourne null pour un effet à base de composite() (cf. needsSegmentation) : les deux
   * mécanismes sont exclusifs, jamais cumulés sur le même effet.
   */
  getCssFilter(_config: TConfig): string | null {
    return null;
  }

  /**
   * Dessine l'effet sur le canvas de préview, une frame à la fois.
   *
   * Groupe "surroundings" : responsable de la BASE entière de la frame — doit la
   * repeindre opaque (couleur, flou, image...) PUIS y découper la personne par-dessus
   * avec drawPersonCutout si `personAlpha` est disponible ET `hidePerson` faux (cf.
   * VideoEffectCompositeArgs.hidePerson — un effet "face" actif qui veut que la
   * personne n'apparaisse pas, ex. Avatar avec son modèle 3D à la place : dans ce cas
   * NE PAS découper/coller la personne, pour que ce fond reste visible entier là où
   * elle se serait trouvée). Appelé seulement s'il est l'effet actif de son groupe ;
   * sinon compositeFrame.ts peint un simple passthrough à sa place.
   *
   * Groupe "face" : vient TOUJOURS après un groupe "surroundings" déjà peint (actif ou
   * passthrough) — ne doit PAS repeindre le cadre entier, seulement ajouter son calque
   * par-dessus (cf. MakeupEffect, qui ne redessine que la peau détourée).
   */
  composite(_args: VideoEffectCompositeArgs<TConfig>): void {}

  abstract renderControls(props: VideoEffectControlProps<TConfig>): React.ReactNode;
}

/**
 * Découpe la personne dans `frame` (via `personAlpha` comme canal alpha) et la dessine
 * sur `ctx`, par-dessus ce qui y est déjà peint (le fond de l'effet). `scratch` sert de
 * brouillon : on y compose le portrait détouré avant de le reposer sur le canvas final,
 * `destination-in` ne pouvant pas s'appliquer directement au canvas de sortie sans en
 * effacer le fond qu'on vient de peindre.
 */
export function drawPersonCutout(
  ctx: CanvasRenderingContext2D,
  frame: HTMLVideoElement,
  personAlpha: HTMLCanvasElement,
  width: number,
  height: number,
  scratch: HTMLCanvasElement,
): void {
  if (scratch.width !== width || scratch.height !== height) {
    scratch.width = width;
    scratch.height = height;
  }
  const sctx = scratch.getContext('2d');
  if (!sctx) return;
  sctx.clearRect(0, 0, width, height);
  sctx.drawImage(frame, 0, 0, width, height);
  sctx.globalCompositeOperation = 'destination-in';
  sctx.drawImage(personAlpha, 0, 0, width, height);
  sctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(scratch, 0, 0, width, height);
}

/**
 * Variante de drawPersonCutout qui FLOUTE la personne au lieu de la coller nette —
 * utilisée par le compositeur (cf. compositeFrame.ts) quand un effet "face" veut
 * cacher la personne (VideoEffect.wantsPersonHidden) mais qu'aucun effet
 * "surroundings" n'est actif pour fournir un fond de remplacement à révéler à sa
 * place (le cas normal — un effet "surroundings" actif doit plutôt sauter SON
 * PROPRE drawPersonCutout, cf. SurroundingsEffect, pour que son fond reste entier).
 * À défaut de mieux ici, flouter reste moins brutal qu'un aplat ou un trou.
 */
let blurWorkCanvas: HTMLCanvasElement | null = null;

/**
 * Dessine `frame` floutée de `blurPx` pixels (à pleine résolution) sur `ctx`, en calculant le
 * flou sur une image réduite HIDE_BODY_BLUR_SCALE fois : un `filter: blur()` sur du
 * 1920×1080 à chaque image saturait le thread principal (Ales-TV, 25/10/2026). Un flou de
 * quelques dizaines de pixels est une basse fréquence : rendu visuellement équivalent.
 */
export function drawBlurredFrame(
  ctx: CanvasRenderingContext2D,
  frame: HTMLVideoElement,
  width: number,
  height: number,
  blurPx: number,
): void {
  const { width: bw, height: bh } = reducedSize(width, height);
  if (!blurWorkCanvas) blurWorkCanvas = document.createElement('canvas');
  if (blurWorkCanvas.width !== bw || blurWorkCanvas.height !== bh) { blurWorkCanvas.width = bw; blurWorkCanvas.height = bh; }
  const bctx = blurWorkCanvas.getContext('2d');
  if (!bctx) {
    ctx.save();
    ctx.filter = `blur(${blurPx}px)`;
    ctx.drawImage(frame, 0, 0, width, height);
    ctx.restore();
    return;
  }
  bctx.clearRect(0, 0, bw, bh);
  bctx.filter = `blur(${blurPx / HIDE_BODY_BLUR_SCALE}px)`;
  bctx.drawImage(frame, 0, 0, bw, bh);
  bctx.filter = 'none';
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(blurWorkCanvas, 0, 0, width, height);
  ctx.restore();
}

export function blurPersonCutout(
  ctx: CanvasRenderingContext2D,
  frame: HTMLVideoElement,
  personAlpha: HTMLCanvasElement,
  width: number,
  height: number,
  scratch: HTMLCanvasElement,
  blurPx = 28,
): void {
  if (scratch.width !== width || scratch.height !== height) {
    scratch.width = width;
    scratch.height = height;
  }
  const sctx = scratch.getContext('2d');
  if (!sctx) return;
  sctx.clearRect(0, 0, width, height);
  // Le flou se fait sur une image RÉDUITE (HIDE_BODY_BLUR_SCALE fois plus petite, rayon
  // divisé d'autant) puis remontée à pleine taille : un flou de 28 px est une basse
  // fréquence, le résultat est visuellement le même, mais un `filter: blur()` sur du
  // 1920×1080 à chaque image saturait le thread principal avec « Cacher le corps »
  // (Ales-TV, 25/10/2026 : 92 % de fil principal, programme à 3,5 i/s). Seul l'Avatar
  // atteint ce chemin (wantsPersonHidden).
  const { width: bw, height: bh } = reducedSize(width, height);
  if (!blurWorkCanvas) blurWorkCanvas = document.createElement('canvas');
  if (blurWorkCanvas.width !== bw || blurWorkCanvas.height !== bh) { blurWorkCanvas.width = bw; blurWorkCanvas.height = bh; }
  const bctx = blurWorkCanvas.getContext('2d');
  if (bctx) {
    bctx.clearRect(0, 0, bw, bh);
    bctx.filter = `blur(${blurPx / HIDE_BODY_BLUR_SCALE}px)`;
    bctx.drawImage(frame, 0, 0, bw, bh);
    bctx.filter = 'none';
    sctx.imageSmoothingEnabled = true;
    sctx.drawImage(blurWorkCanvas, 0, 0, width, height);
  } else {
    sctx.filter = `blur(${blurPx}px)`;
    sctx.drawImage(frame, 0, 0, width, height);
    sctx.filter = 'none';
  }
  sctx.globalCompositeOperation = 'destination-in';
  sctx.drawImage(personAlpha, 0, 0, width, height);
  sctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(scratch, 0, 0, width, height);
}
